import { useEffect, useState } from 'react';

import { USCityView } from '@zola/svc-marketplace-ts-types';
import { CheckboxList } from '@zola/zola-ui/src/components/Form/CheckboxList';
import FinalFormDatePickerV2 from '@zola/zola-ui/src/components/Form/DatePickerInputV2/FinalFormDatePickerV2';
import { composeValidators } from '@zola/zola-ui/src/components/Form/util/composeValidators';

import type { FormApi } from 'final-form';
import _get from 'lodash/get';
import { Field } from 'react-final-form';
import { FieldArray } from 'react-final-form-arrays';

import {
  REQUIRED,
  DATE_AFTER_NOW,
  NOT_EMPTY_LOCATION,
} from '~/components/common/form/commonValidations';
import {
  AutoSuggestLocationFieldAdapter,
  FormCheckbox,
} from '~/components/common/form/FinalForm/Inputs';
import { AccountWeddingPreferencesView } from '~/types/responseTypes';
import { formatLocation } from '~/util/formatters';
import STYLE_DATA from '~/util/styleData';

import styles from './questions.module.less';

/**
 * The form values used with this component must have this structure
 */
export interface WeddingLocationFormValues {
  weddingLocation?: {
    city: string;
    stateProvince: string;
  };
}
interface WeddingQuestionsProps<T extends WeddingLocationFormValues> {
  form: FormApi<T>;
  weddingPreferences: AccountWeddingPreferencesView;
  setNewMarket: (key: number | null) => void;
}

export const WeddingQuestions = <T extends WeddingLocationFormValues>({
  form,
  weddingPreferences,
  setNewMarket,
}: WeddingQuestionsProps<T>): JSX.Element => {
  const {
    mutators: { setField },
  } = form;
  const [weddingLocation, setWeddingLocation] = useState('');

  /**
   * populate location when autosuggest selected
   *
   * We add withMarket to the auto-suggest, so we get back a vendorMarket in the suggestion.
   */
  const handleLocationSelected = (e: Event, { suggestion }: { suggestion: USCityView }) => {
    const { name, stateCode, vendorMarket } = suggestion;
    setField('weddingLocation', {
      city: name,
      stateProvince: stateCode,
    });
    setNewMarket(vendorMarket?.id ?? null);
  };

  const [flexibleOnDate, setFlexibleOnDate] = useState<boolean | null>(null);

  const [initialized, setInitialized] = useState(false);

  /**
   * Run once to initialize the form values from wedding preferences
   */
  useEffect(() => {
    if (weddingPreferences && !initialized) {
      if (weddingPreferences.flexibleOnDate !== (null || undefined)) {
        setField('flexibleOnDate', setFlexibleOnDate(weddingPreferences.flexibleOnDate));
      }
      if (weddingPreferences.address) {
        const location = {
          city: _get(weddingPreferences, 'address.city', ''),
          stateProvince: _get(weddingPreferences, 'address.stateProvince', ''),
        };

        const locationAsString = formatLocation(location);
        setField('weddingLocation', location);
        setWeddingLocation(locationAsString);
      }
      setField(
        'overallStyle',
        weddingPreferences.facetsView?.map((facet) => facet.key)
      );

      setInitialized(true);
    }
  }, [weddingPreferences, setField, initialized]);

  const styleOptions = STYLE_DATA.map((data) => ({ label: data.label, value: data.value }));

  const handleDateFlexibleChange = (event: React.SyntheticEvent<HTMLInputElement>) => {
    const { currentTarget } = event;
    setFlexibleOnDate(currentTarget.checked);
    setField('flexibleOnDate', currentTarget.checked);
  };

  /**
   * Handler passed (eventually) to the AutoSuggest input component.  We use this
   * to clear/reset the form values when the input changes.  For example, if the
   * input is set to "Boston, MA" and the couple backspaces a character, or erases
   * the text input, we reset the form values so we can validate that a location
   * is required.
   *
   * @param event
   * @param payload
   * @returns
   */
  const onLocationChange = (event: Event, payload: any) => {
    const { method } = payload;
    if (method === 'click') {
      // A click means they used the mouse to pick from the dropdown
      return;
    }
    setField('weddingLocation', undefined);
    setNewMarket(null);
  };

  return (
    <div className={styles.weddingQuestions}>
      <div className={styles.formField}>
        <FinalFormDatePickerV2
          name="weddingDate"
          id="weddingDate"
          label="Wedding Date"
          className={styles.weddingDate}
          validate={DATE_AFTER_NOW}
        />
      </div>
      <div className={styles.formField}>
        <FormCheckbox
          name="flexibleOnDate"
          label="This date isn’t set yet and can change"
          checked={!!flexibleOnDate}
          onClick={handleDateFlexibleChange}
        />
      </div>
      <div className={styles.formField}>
        <Field
          name="weddingLocation"
          id="weddingLocation"
          labelText="Wedding Location"
          component={AutoSuggestLocationFieldAdapter}
          labelFor="weddingLocation"
          input={{
            name: 'weddingLocation',
            value: weddingLocation,
            showIcon: true,
            inputV2: true,
          }}
          allowBlank
          onInputChange={onLocationChange}
          onSuggestionSelected={handleLocationSelected}
          withMarkets
          validate={composeValidators(NOT_EMPTY_LOCATION)}
        />
      </div>
      <fieldset>
        <legend className={styles.label}>Overall Wedding Style</legend>
        <FieldArray
          options={styleOptions}
          name="overallStyle"
          id="overallStyle"
          validate={REQUIRED}
        >
          {({ fields, meta }) => (
            <CheckboxList fields={fields} meta={meta} options={styleOptions} />
          )}
        </FieldArray>
      </fieldset>
    </div>
  );
};
